import { Component, inject } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';
import { TranslocoModule } from '@jsverse/transloco';
import { CommonModule } from '@angular/common';

// INTERFACES
import { IDid, IDids } from '../../models/dids.model';
import { IPackagePrice } from '../../models/package-price.model';
// COMPONENTS
import { SpinnerSmallComponent } from '../../core/shared/loading/spinner-small/spinner-small.component';
import { NeedMoreInfoFormComponent } from '../../core/shared/choose-your-number/need-more-info-form/need-more-info-form.component';
// SERVICES
import { SubmitOrderService } from '../../services/submit-order/submit-order.service';
import { GoogleTagManagerService } from '../../services/google-tag-manager/google-tag-manager.service';
import { ConversionApiService } from '../../services/conversion-api/conversion-api.service';
import { ChooseNumberService } from '../../services/choose-number/choose-number.service';
// PIPES
import { SlidersPipe } from '../../pipes/sliders.pipe';
import { FlagsPipe } from '../../pipes/flags.pipe';
// MODELS
import { IDidCountries, IPackageCountries } from '../../models/countries.model';
import { LoadingService } from '../../../../../lib-shared/src/lib/services/loading.service';
import { PopUpService } from 'lib-shared';

@Component({
  selector: 'app-choose-number-page',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NeedMoreInfoFormComponent,
    TranslocoModule,
    SlidersPipe,
    FlagsPipe,
    SpinnerSmallComponent,
  ],
  templateUrl: './choose-number-page.component.html',
  styleUrl: './choose-number-page.component.scss',
})
export class ChooseNumberPageComponent {
  router = inject(Router);
  route = inject(ActivatedRoute);
  chooseNumberService = inject(ChooseNumberService);
  gtmService = inject(GoogleTagManagerService);
  submitOrderService = inject(SubmitOrderService);
  conversionApiService = inject(ConversionApiService);
  loadingService = inject(LoadingService);
  popUpService = inject(PopUpService);

  didCountryName: string = this.route.snapshot.params['didCountry'];
  packageCountryName: string = this.route.snapshot.params['packageCountry'];

  didCountry: IDidCountries | null = null;
  packageCountry: IPackageCountries | null = null;

  didNumbers: IDids[] = [];

  showPopover: boolean = false;
  showNumbers: boolean = false;

  packagePrice: IPackagePrice | null = null;
  priceInteger: string | null = null;
  priceDecimal: string | null = null;

  constructor(private titleService: Title, private meta: Meta) {}

  async ngOnInit(): Promise<void> {
    this.loadingService.setLoading(true);

    try {
      const pageData = await this.chooseNumberService.getPageData(
        this.didCountryName,
        this.packageCountryName
      );
      // countries data
      this.didCountry = pageData.didCountry;
      this.packageCountry = pageData.packageCountry;
      // did number availables
      this.didNumbers = pageData.didNumbers;
      // price data
      this.packagePrice = pageData.packagePrice;
      this.priceInteger = pageData.priceInteger;
      this.priceDecimal = pageData.priceDecimal;

      this.updateMetaTags();
    } catch (error) {
      console.error('Error fetching page data:', error);
      this.popUpService.openPopUp('server-error');
      this.router.navigate(['']);
    } finally {
      this.loadingService.setLoading(false);
    }
  }

  onChooseVNumber(didData: IDid) {
    this.submitOrderService.setOrderData({ didData: didData });
    this.gtmService.addToCart(
      didData,
      this.packagePrice?.price,
      this.packageCountry.packageId,
      this.packageCountryName
    );
    this.conversionApiService.trackEvent('AddToCart');

    this.router.navigate(['personal-information']);
  }

  showPeriodPopover() {
    this.showPopover = true;
    setTimeout(() => {
      this.showPopover = false;
    }, 2500);
  }

  updateMetaTags() {
    const countryName = this.didCountry.countryName;
    const title = `Choose a Virtual Number in ${countryName} | VNumber`;
    const description = `Get a local virtual phone number in ${countryName}. Choose your plan and stay connected globally with VNumber.`;
    const canonicalUrl = `https://vnumber.com${this.router.url}`;

    this.titleService.setTitle(title);
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({
      name: 'keywords',
      content: `virtual number, vnumber, phone number ${countryName}, ${countryName} virtual number`,
    });
    this.meta.updateTag({ rel: 'canonical', href: canonicalUrl });
  }

  // header(STATE/CITY) should be shown for the current item
  shouldShowHeader(item: IDids, index: number): boolean {
    return (
      // Check if we are at the country level (no states) or if it is the first state.
      this.didNumbers.findIndex((vn) => vn.state === item.state) === index
    ); 
  }
}
